angular.module('bizmetryApp').factory('ApiCollectionService', function($http, CONFIG) {

  const BASE_URL = CONFIG.BIZMETRY_BACKEND_URL + '/v1/api/bizmetry/api-collection';

  return {

    // ============================================
    // GET ALL  (paged + search + sort)
    // ============================================

    /**
     * Fetches a paged, filtered and sorted list of api collections.
     *
     * @param {string} accountId
     * @param {Object} options
     *   - page       {number}   0-based page index
     *   - size       {number}   page size
     *   - search     {string}   keyword filter (name, version, status)
     *   - sortField  {string}   collection_name | api_version | created_ts | updated_ts | status
     *   - sortDir    {string}   ASC | DESC
     *
     * @returns {Promise<Response>}
     *   status 200: { content, page, size, totalElements, totalPages, first, last }
     *   status 204: no content
     */
    getAll: function(accountId, options) {
      const params = { account_id: accountId };
      const o = options || {};

      if (o.page      != null)                params.page       = o.page;
      if (o.size      != null)                params.size       = o.size;
      if (o.search    && o.search.trim())     params.search     = o.search.trim();
      if (o.sortField && o.sortField.trim())  params.sort_field = o.sortField.trim();
      if (o.sortDir   && o.sortDir.trim())    params.sort_dir   = o.sortDir.trim().toUpperCase();
      if (o.groupByFamily)                    params.group_by_family = true;
      if (o.includeReferences === false)      params.include_references = false;  // skip biz event references

      return $http({
        method: 'GET',
        url:    BASE_URL,
        params,
        headers: { 'Content-Type': 'application/json' }
      }).then(response => response);  // ← devuelve response completa para leer el status
    },

    // ============================================
    // GET BY ID
    // ============================================

    // Operation changes from collection version fromId to toId (same account):
    // { from_version, to_version, changes: [ApiOperationChange], changes_truncated }
    getChanges: function(toId, fromId) {
      return $http({
        method: 'GET',
        url: BASE_URL + '/' + toId + '/changes',
        params: { from: fromId },
        headers: { 'Content-Type': 'application/json' }
      }).then(response => response.data);
    },

    getById: function(collectionId) {
      return $http({
        method: 'GET',
        url: BASE_URL + '/' + collectionId,
        headers: { 'Content-Type': 'application/json' }
      }).then(response => response.data);
    },

    // ============================================
    // CREATE — JSON (kept for backward compat / small specs)
    // ============================================

    create: function(payload) {
      return $http({
        method: 'POST',
        url: BASE_URL,
        data: payload,
        headers: { 'Content-Type': 'application/json' }
      }).then(response => response);
    },

    // ============================================
    // CREATE — Multipart (spec sent as file part,
    //   bypasses ModSecurity SecRequestBodyNoFilesLimit)
    //
    // Expected FormData fields:
    //   collection_name  {string}
    //   description      {string}
    //   swagger_source   {string}  JSON-serialised SwaggerSourceDTO
    //   spec_file        {File|Blob}  raw YAML or JSON spec
    // ============================================

    createMultipart: function(formData) {
      return $http({
        method: 'POST',
        url: BASE_URL + '/multipart',
        data: formData,
        headers: {
          // Let the browser set Content-Type with the multipart boundary
          'Content-Type': undefined
        },
        // Skip AngularJS default JSON serialisation
        transformRequest: angular.identity
      }).then(response => response);
    },

    // ============================================
    // UPDATE (full)
    // ============================================

    update: function(collectionId, payload) {
      return $http({
        method: 'PUT',
        url: BASE_URL + '/' + collectionId,
        data: payload,
        headers: { 'Content-Type': 'application/json' }
      }).then(response => response.data);
    },

    // ============================================
    // PATCH (partial)
    // ============================================

    /**
     * Patchable fields: collection_name, api_version, swagger, status
     */
    patch: function(collectionId, fields) {
      return $http({
        method: 'PATCH',
        url: BASE_URL + '/' + collectionId,
        data: fields,
        headers: { 'Content-Type': 'application/json' }
      }).then(response => response.data);
    },

    // ============================================
    // FORK — edit of a PUBLISHED collection → new DRAFT version
    // ============================================

    /**
     * Same fields as patch (description, swagger, swagger_source). The server
     * creates a new DRAFT collection with a bumped version (major on breaking
     * changes, minor otherwise) and resolves with it; the published one stays.
     */
    fork: function(collectionId, fields) {
      return $http({
        method: 'POST',
        url: BASE_URL + '/' + collectionId + '/fork',
        data: fields,
        headers: { 'Content-Type': 'application/json' }
      }).then(response => response.data);
    },

    // ============================================
    // VALIDATE SWAGGER (dry run — Swagger editor)
    // ============================================

    /**
     * Server-side validation of a candidate swagger for an existing collection,
     * without saving it. Resolves to
     *   { valid, message, operations_count, changes: [ApiOperationChange], changes_total }
     */
    validateSwagger: function(collectionId, swagger) {
      return $http({
        method: 'POST',
        url: BASE_URL + '/' + collectionId + '/swagger/validate',
        data: swagger,
        headers: { 'Content-Type': 'application/json' },
        bmSkipLoader: true
      }).then(response => response.data);
    },

    /**
     * Same dry-run validation for a contract written from scratch
     * ("Create from blank"), before the collection exists.
     */
    validateNewSwagger: function(swagger) {
      return $http({
        method: 'POST',
        url: BASE_URL + '/swagger/validate',
        data: swagger,
        headers: { 'Content-Type': 'application/json' },
        bmSkipLoader: true
      }).then(response => response.data);
    },

    // ============================================
    // ADVANCE STATUS
    // ============================================

    advanceStatus: function(collection) {
      const transitions = {
        'DRAFT':          'PENDING_REVIEW',
        'PENDING_REVIEW': 'REVIEWED',
        'REVIEWED':       'PUBLISHED',
        'REJECTED':       'PENDING_REVIEW'
      };

      const nextStatus = transitions[collection.status];
      if (!nextStatus) {
        return Promise.reject(new Error('No valid transition from status: ' + collection.status));
      }

      return this.patch(collection.collection_id, { status: nextStatus });
    },

    // ============================================
    // RETIRE
    // ============================================

    retire: function(collectionId) {
      return this.patch(collectionId, { status: 'RETIRED' });
    },

    // ============================================
    // DELETE
    // ============================================

    delete: function(collectionId) {
      return $http({
        method: 'DELETE',
        url: BASE_URL + '/' + collectionId,
        headers: { 'Content-Type': 'application/json' }
      }).then(response => response.data);
    }

  };
});